import { Box, Paper, Grid, List, ListItem, Avatar } from "@mui/material";
import {
  AttachMoney as DollarIcon,
  Person as UserIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { Text } from "@src/lib/ui-react";

import { formatDateTime } from "@clipboard-health/date-time";
import { TimelineEvent } from "./type";
import { getEventBackgroundColor } from "./utils";

export function ShiftTimeline({ events }: { events: TimelineEvent[] }) {
  return (
    <Grid item xs={12} lg={8}>
      <Paper>
        <Box sx={{ padding: "12px 16px", borderBottom: "1px solid #e0e0e0" }}>
          <Text variant="subtitle1" bold>
            Shift Timeline
          </Text>
          <Text variant="caption" color="secondary">
            All activities related to this shift
          </Text>
        </Box>

        <Box sx={{ padding: "12px 16px" }}>
          {events.length === 0 ? (
            <Box sx={{ padding: 3, textAlign: "center" }}>
              <Text color="secondary">No timeline events found for this shift.</Text>
            </Box>
          ) : (
            <List sx={{ padding: 0 }}>
              {events.map((event, eventIdx) => (
                <ListItem
                  key={event.id}
                  alignItems="flex-start"
                  sx={{
                    padding: 0,
                    paddingBottom: 2,
                    position: "relative",
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      left: 16,
                      top: 32,
                      bottom: 0,
                      width: 2,
                      bgcolor: eventIdx === events.length - 1 ? "transparent" : "#e0e0e0",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: getEventBackgroundColor(event.type),
                        color: "inherit",
                        marginRight: 2,
                        marginTop: 0.5,
                      }}
                    >
                      {event.icon}
                    </Avatar>

                    <Box sx={{ minWidth: 0, flexGrow: 1, paddingY: 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Box>
                          <Text variant="body2" bold data-testid={`event-title-${event.type}`}>
                            {event.title}
                          </Text>
                          <Text variant="caption" color="secondary">
                            {event.description}
                          </Text>
                        </Box>
                        <Text
                          variant="caption"
                          color="secondary"
                          sx={{ whiteSpace: "nowrap", marginLeft: 2 }}
                        >
                          {formatDateTime(event.time)}
                        </Text>
                      </Box>
                      {event.details && Object.keys(event.details).length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            pt: 1,
                            borderTop: "1px dashed #e0e0e0",
                            maxWidth: "90%",
                          }}
                        >
                          {event.type === "rate" && event.details.notes && (
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <DollarIcon sx={{ fontSize: 14, color: "#2e7d32", mr: 0.5 }} />
                              <Text variant="caption">{event.details.notes}</Text>
                            </Box>
                          )}

                          {event.type === "payment" && event.details.isPaidInstantly && (
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <DollarIcon sx={{ fontSize: 14, color: "#2e7d32", mr: 0.5 }} />
                              <Text variant="caption">Instant pay enabled</Text>
                            </Box>
                          )}

                          {event.type === "cancel" && event.details.reason && (
                            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
                              <InfoIcon sx={{ fontSize: 14, color: "#d32f2f", mr: 0.5, mt: 0.3 }} />
                              <Text variant="caption">Reason: {event.details.reason}</Text>
                            </Box>
                          )}

                          {event.details.performedBy && (
                            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                              <UserIcon sx={{ fontSize: 14, color: "#757575", mr: 0.5 }} />
                              <Text variant="caption">By: {event.details.performedBy}</Text>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>
    </Grid>
  );
}
